import { useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import BoundingBoxLabelMarker from './components/BoundingBoxLabelMarker';
import { GEOJSON_PROPERTY } from './constants';
import type { Marker, UpdateMarkerParams, UseMarkers } from './types';

const useMarkers = (): UseMarkers => {
    const markerMap = useRef<Record<string, Marker>>({});
    const featureMarkerMap = useRef<Record<string, Marker>>({});

    const addMarker = ({ feature, position, rotation, text }: UpdateMarkerParams) => {
        const isStatic = feature.properties?.[GEOJSON_PROPERTY.IS_STATIC];
        const newMarker: Marker = {
            featureId: feature.id as string,
            id: uuidv4(),
            isStatic,
            markerElement: BoundingBoxLabelMarker({ text }),
            position,
            rotation,
            text
        };

        markerMap.current = {
            ...markerMap.current,
            [newMarker.id]: newMarker
        };

        featureMarkerMap.current = {
            ...featureMarkerMap.current,
            [feature.id as string]: newMarker
        };

        return newMarker;
    };

    const updateMarker = ({ feature, position, rotation, text }: UpdateMarkerParams) => {
        const marker = featureMarkerMap.current[feature.id as string];

        if (!marker) {
            addMarker({ feature, position, rotation, text });

            return;
        }

        const isFeatureSelected = feature.properties?.[GEOJSON_PROPERTY.IS_SELECTED];
        const isFeatureActive = feature.properties?.[GEOJSON_PROPERTY.IS_ACTIVE_BOUNDARY];

        marker.position = position;
        marker.rotation = rotation;
        marker.text = text;

        marker.markerElement = BoundingBoxLabelMarker({
            isActive: isFeatureActive,
            isSelected: isFeatureSelected,
            text
        });
    };

    const removeMarkerById = (markerId: string) => {
        const marker = markerMap.current[markerId];

        if (!marker) {
            return;
        }

        delete markerMap.current[markerId];

        const { featureId } = marker;

        delete featureMarkerMap.current[featureId];
    };

    const clear = () => {
        markerMap.current = {};
        featureMarkerMap.current = {};
    };

    const getMarkerById = (markerId: string) => markerMap.current[markerId];

    const getMarkerByFeatureId = (featureId: string) => featureMarkerMap.current[featureId];

    const removeMarkerByFeatureId = (featureId: string) => {
        const marker = featureMarkerMap.current[featureId];

        if (!marker) {
            return;
        }

        const { id } = marker;

        if (!id) {
            return;
        }

        removeMarkerById(id);
    };

    return {
        addMarker,
        clear,
        getMarkerByFeatureId,
        getMarkerById,
        markerMap: markerMap.current,
        removeMarkerByFeatureId,
        removeMarkerById,
        updateMarker
    };
};

export default useMarkers;
